import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { DailyScrumWithItems } from '../../../common/types/databaseTypes';
import { useSafeQuery } from '@/common/hooks/useSafeQuery';
import { supabase } from '@tickitacka/shared';
import {
    CreateDailyScrumItemParams,
    DailyScrumItemUpdate,
    dailyScrumService,
} from './dailyScrumService';

// 데일리스크럼 쿼리 키 상수
export const DAILYSCRUM_KEYS = {
    all: ['dailyScrums'] as const,
    byTeamId: (teamId: string) => [...DAILYSCRUM_KEYS.all, teamId] as const,
    byDate: (teamId: string, date: string) =>
        [...DAILYSCRUM_KEYS.byTeamId(teamId), date] as const,
};

/**
 * 특정 날짜의 데일리스크럼 정보를 가져오는 훅 (실시간 구독 지원)
 * @param teamId 팀 ID
 * @param date 조회할 날짜 (YYYY-MM-DD 형식)
 */
export const useDailyScrumByDate = (
    teamId: string,
    date: string
): UseQueryResult<DailyScrumWithItems | null> => {
    const queryClient = useQueryClient();
    const queryKey = React.useMemo(
        () => DAILYSCRUM_KEYS.byDate(teamId, date),
        [teamId, date]
    );

    // 실시간 구독 설정
    useEffect(() => {
        if (!teamId || !date) return;

        // 테이블 변경 구독 함수
        const subscribeToTable = (
            channelName: string,
            tableName: string,
            filter?: string
        ) => {
            return supabase
                .channel(channelName)
                .on(
                    'postgres_changes',
                    {
                        event: '*',
                        schema: 'public',
                        table: tableName,
                        ...(filter ? { filter } : {}),
                    },
                    payload => {
                        console.log(`${tableName} 테이블 변경 감지:`, payload);
                        void queryClient.invalidateQueries({ queryKey });
                    }
                )
                .subscribe();
        };

        // 필요한 테이블들 구독
        const dailyScrumChannel = subscribeToTable(
            'dailyscrum-by-date-changes',
            'daily_scrum',
            `team_id=eq.${teamId} AND date=eq.${date}`
        );

        const itemsChannel = subscribeToTable(
            'dailyscrum-items-by-date-changes',
            'daily_scrum_item'
        );

        const commentsChannel = subscribeToTable(
            'dailyscrum-comments-by-date-changes',
            'daily_scrum_comment'
        );

        const emojiReactionsChannel = subscribeToTable(
            'dailyscrum-emoji-reactions-by-date-changes',
            'daily_scrum_item_emoji_reaction'
        );

        // 컴포넌트 언마운트 시 구독 해제
        return () => {
            void supabase.removeChannel(dailyScrumChannel);
            void supabase.removeChannel(itemsChannel);
            void supabase.removeChannel(commentsChannel);
            void supabase.removeChannel(emojiReactionsChannel);
        };
    }, [teamId, date, queryClient, queryKey]);

    // 기본 쿼리 로직
    return useSafeQuery({
        queryKey,
        queryFn: () => {
            if (!teamId || !date) return null;
            return dailyScrumService.getDailyScrumByDate(date, teamId);
        },
        enabled: !!teamId && !!date,
    });
};

// ==================== 데일리 스크럼 아이템 뮤테이션 ====================

/**
 * 데일리 스크럼 아이템을 생성하는 훅
 */
export const useCreateDailyScrumItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateDailyScrumItemParams) =>
            dailyScrumService.createDailyScrumItem(data),
        onSuccess: newItem => {
            void queryClient.invalidateQueries({
                queryKey: DAILYSCRUM_KEYS.all,
            });
        },
    });
};

/**
 * 데일리 스크럼 아이템을 수정하는 훅
 */
export const useUpdateDailyScrumItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string;
            data: DailyScrumItemUpdate;
        }) => dailyScrumService.updateDailyScrumItem(id, data),
        onSuccess: updatedItem => {
            void queryClient.invalidateQueries({
                queryKey: DAILYSCRUM_KEYS.all,
            });
        },
    });
};

/**
 * 데일리 스크럼 아이템을 삭제하는 훅
 */
export const useDeleteDailyScrumItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => dailyScrumService.deleteDailyScrumItem(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: DAILYSCRUM_KEYS.all,
            });
        },
    });
};
